/**
 * Single source of truth for navigation, breadcrumbs and quick-nav search.
 * Adding a route means adding it here, not in three places.
 */

import {
  Activity,
  BarChart3,
  Brain,
  Layers,
  LayoutDashboard,
  Receipt,
  Settings,
  Shield,
  Wallet,
  Briefcase,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export interface NavItem {
  label: string;
  href: string;
  icon: LucideIcon;
  /** Extra paths that should light up this nav entry. */
  aliases?: string[];
  keywords?: string;
}

export interface NavSection {
  title: string;
  items: NavItem[];
}

export const NAV_SECTIONS: NavSection[] = [
  {
    title: 'Trading',
    items: [
      { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, keywords: 'overview home summary' },
      { label: 'Orders', href: '/orders', icon: Receipt, aliases: ['/trading'], keywords: 'order book place trade buy sell ticket' },
      { label: 'Market Watch', href: '/market', icon: Activity, keywords: 'watchlist quotes depth ltp' },
      { label: 'Option Chain', href: '/options', icon: Layers, keywords: 'options chain strikes straddle strangle spread pcr max pain oi' },
    ],
  },
  {
    title: 'Portfolio',
    items: [
      { label: 'Holdings & Positions', href: '/portfolio', icon: Briefcase, keywords: 'holdings positions pnl' },
      { label: 'Funds', href: '/funds', icon: Wallet, keywords: 'margin cash collateral limits' },
    ],
  },
  {
    title: 'Automation',
    items: [
      { label: 'Strategies', href: '/strategies', icon: Brain, keywords: 'ema rsi macd vwap bollinger oi open interest signals' },
      { label: 'Risk Engine', href: '/risk', icon: Shield, keywords: 'kill switch circuit breaker limits' },
    ],
  },
  {
    title: 'Insights',
    items: [{ label: 'Analytics', href: '/analytics', icon: BarChart3, keywords: 'backtest equity curve metrics' }],
  },
];

export const SETTINGS_ITEM: NavItem = {
  label: 'Settings',
  href: '/settings',
  icon: Settings,
  keywords: 'broker connection profile account',
};

export const ALL_NAV_ITEMS: NavItem[] = [...NAV_SECTIONS.flatMap((s) => s.items), SETTINGS_ITEM];

/** Section title + page label for the current path, for breadcrumbs. */
export function locateRoute(pathname: string): { section?: string; item?: NavItem } {
  for (const section of NAV_SECTIONS) {
    const item = section.items.find((i) => i.href === pathname || i.aliases?.includes(pathname));
    if (item) return { section: section.title, item };
  }
  if (pathname === SETTINGS_ITEM.href) return { section: 'Account', item: SETTINGS_ITEM };
  return {};
}

export function isNavItemActive(item: NavItem, pathname: string): boolean {
  return item.href === pathname || (item.aliases?.includes(pathname) ?? false);
}
